import {
  Dialog,
  Box,
  Button,
  makeStyles,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@material-ui/core"
import { useEffect, useState } from "react"

import { useApi } from "../../utils/API"
import { isEmailValid } from "../../utils/validators"
import { PrimaryButton } from "../atoms/button/Button"
import PhoneInput from "../atoms/input/PhoneInput"
import Input from "../atoms/input/Input"
import CloseIcon from "../atoms/CloseIcon"

interface InviteClientDialogProps {
  open: boolean
  onClose: () => void
}

export default function InviteClientDialog({
  open,
  onClose,
}: InviteClientDialogProps) {
  const classes = useStyles()
  const api = useApi()

  const [smsAgreement, setSmsAgreement] = useState<boolean>(false)
  const [email, setEmail] = useState<string>("")
  const [phoneNumber, setPhoneNumber] = useState<string>("")
  const [isPhoneValid, setIsPhoneValid] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const [emailError, setEmailError] = useState<string | null>(null)
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [networkError, setNetworkError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const resetForm = () => {
    setEmail("")
    setSmsAgreement(false)
    setPhoneNumber("")
    setIsPhoneValid(false)
    setIsSubmitting(false)
    setEmailError(null)
    setPhoneError(null)
    setNetworkError(null)
    setSuccessMessage(null)
  }

  useEffect(() => {
    if (!open) {
      resetForm()
    }
  }, [open])

  const validateEmail = (value: string) => {
    const trimmed = value.trim()
    if (!trimmed) return null
    return isEmailValid(trimmed) ? null : "Please enter a valid email address."
  }

  const inviteFailedError =
    "We couldn't send the invite. Did you enter the correct contact information?"

  const createInvite = async () => {
    setEmailError(null)
    setPhoneError(null)
    setNetworkError(null)
    setSuccessMessage(null)

    const trimmedEmail = email.trim()
    const emailValidationError = validateEmail(email)

    const phoneValidationError =
      phoneNumber && !isPhoneValid ? "Please enter a valid phone number." : null

    setEmailError(emailValidationError)
    setPhoneError(phoneValidationError)

    if (emailValidationError || phoneValidationError) return

    setIsSubmitting(true)
    try {
      const response = await api.createInviteCode(phoneNumber, trimmedEmail)
      if (!response || response.status < 200 || response.status >= 300) {
        setNetworkError(inviteFailedError)
        return
      }

      setSuccessMessage("Invite sent successfully.")
    } catch (error) {
      setNetworkError(inviteFailedError)
    } finally {
      setIsSubmitting(false)
    }
  }

  const canAttemptSubmit = (phoneNumber && smsAgreement) || email
  const canSubmit = canAttemptSubmit && !isSubmitting && !successMessage

  return (
    <Dialog fullWidth={true} maxWidth="xs" open={open} onClose={onClose}>
      <Box className={classes.dialogContent}>
        <Box className={classes.dialogCloseContainer}>
          <Button className={classes.closeButton} onClick={onClose}>
            <CloseIcon />
          </Button>
        </Box>
        <Typography variant="h2">Invite a Client</Typography>

        <FormControl className={classes.formGroup}>
          <Input
            value={email}
            onChange={(e) => {
              const nextEmail = e.target.value
              setEmail(nextEmail)

              if (successMessage) {
                setSuccessMessage(null)
              }

              if (emailError) {
                const trimmed = nextEmail.trim()
                if (!trimmed || isEmailValid(trimmed)) {
                  setEmailError(null)
                }
              }
              if (networkError) {
                setNetworkError(null)
              }
            }}
            onBlur={() => {
              setEmailError(validateEmail(email))
            }}
            label="Client's Email"
            type="email"
            error={Boolean(emailError)}
            helperText={emailError || undefined}
            FormHelperTextProps={{
              id: "invite-client-email-error",
              role: "alert",
            }}
          />
        </FormControl>

        <FormControl className={classes.formGroup}>
          <FormLabel id="does-user-have-phone">
            Does your client have access to their own mobile phone and agree to
            receive SMS messages?
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby="does-user-have-phone"
            name="yes-no"
            value={smsAgreement ? "Yes" : "No"}
            onChange={(e) => {
              const agreed = e.target.value === "Yes"
              setSmsAgreement(agreed)

              setPhoneError(null)
              setNetworkError(null)
              setSuccessMessage(null)

              if (!agreed) {
                setPhoneNumber("")
                setIsPhoneValid(false)
              }
            }}
          >
            <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="No" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>

        <FormControl className={classes.formGroup}>
          <PhoneInput
            isDisabled={!smsAgreement}
            phoneErrorMessage={phoneError}
            label="Client's Phone Number"
            onChangeNumber={(num: string) => {
              setPhoneNumber(num)

              if (successMessage) setSuccessMessage(null)
              if (phoneError) setPhoneError(null)
              if (networkError) setNetworkError(null)
            }}
            onChangeValidity={(valid: boolean) => setIsPhoneValid(valid)}
          />
        </FormControl>

        <FormControl className={classes.formGroup}>
          <PrimaryButton onClick={createInvite} disabled={!canSubmit}>
            Invite Client
          </PrimaryButton>
          {successMessage && (
            <Typography align="center" role="status" style={{ color: "green" }}>
              {successMessage}
            </Typography>
          )}
          {networkError && (
            <Typography color="error" align="center" role="alert">
              {networkError}
            </Typography>
          )}
        </FormControl>
      </Box>
    </Dialog>
  )
}

const useStyles = makeStyles((theme) => ({
  dialogCloseContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(1),
  },
  dialogContent: {
    padding: theme.spacing(2),
  },
  closeButton: {
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  formGroup: {
    marginTop: theme.spacing(2),
    width: "100%",
  },
}))
